import React, { forwardRef, useImperativeHandle } from 'react';
import { FormProvider } from 'react-hook-form';
import * as yup from 'yup';
import { ExtraFieldInternalValue } from '../extraFieldInternalValue';
import { useExtraFieldsFormValidation } from '../useExtraFieldsFormValidation';
import { useObjectExtraFields } from '../useObjectExtraFields';
import {
  ExtraFieldsArea,
  Props as ExtraFieldsAreaProps,
} from './ExtraFieldsArea';
import { Loading } from './ExtraFieldsAreaContent';
import { useExtraFieldsFormSimple } from './useExtraFieldsFormSimple';

type Props = ExtraFieldsAreaProps & {
  onChange: (
    extraFields: ExtraFieldInternalValue[],
    recordTypeName: string | undefined,
  ) => void;
  values?: readonly ExtraFieldInternalValue[];
  recordTypeName: string | null;
};

export type ExtraFieldsAreaSimpleRef = {
  triggerValidation: () => Promise<boolean>;
};

export const ExtraFieldsAreaSimple = forwardRef<
  ExtraFieldsAreaSimpleRef,
  Props
>(({ onChange, values, recordTypeName, ...restProps }, ref) => {
  const { rawObjectExtraFields, getFormFields, loading } = useObjectExtraFields(
    restProps.objectApiName,
  );
  const formFields = getFormFields(restProps.formId);
  const { applyExtraFieldsValidationSchema } = useExtraFieldsFormValidation({
    extraFieldFormInfos: formFields,
    contextParams: restProps.contextParams ?? [],
  });
  const validationSchema = applyExtraFieldsValidationSchema(yup.object());

  const { formMethods } = useExtraFieldsFormSimple({
    values,
    recordTypeName,
    availableRecordTypes: rawObjectExtraFields?.availableRecordTypes ?? [],
    onChange,
    validationSchema,
    inputsMounted: !loading && !!rawObjectExtraFields && formFields.length > 0,
  });

  const { trigger } = formMethods;
  useImperativeHandle(
    ref,
    () => ({
      triggerValidation: () =>
        loading || !rawObjectExtraFields ? Promise.resolve(true) : trigger(),
    }),
    [loading, rawObjectExtraFields, trigger],
  );

  if (loading) {
    return <Loading />;
  }
  if (!rawObjectExtraFields) {
    return null;
  }

  return (
    <FormProvider {...formMethods}>
      <ExtraFieldsArea {...restProps} />
    </FormProvider>
  );
});

ExtraFieldsAreaSimple.displayName = 'ExtraFieldsAreaSimple';
